import { Formik } from 'formik'
import { Form } from '../Form/Form'
import { Button } from 'blaze-ui/Button'
import { Header } from 'components/Wizards/ContentPlanning/Header/Header'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Sections } from 'components/Wizards/Wizard/Form/Sections'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import {
  ConditionalLanguageInputField as LanguageInputField,
  ConditionalInlineLanguageInputField as InlineLanguageInputField,
} from '../Wizard/Form/LanguageInputField/ConditionalLanguageInputField'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { StepSchema } from 'components/Wizards/schemas/Types'
import { useFetchReference } from 'hooks/useFetchReference'
import { useState } from 'react'
import { scrapeErrorStep, topicStep } from 'components/Wizards/schemas/ContentPlanSchema'
import Styles from './ContentPlanWizard.module.scss'
import { isUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { Processing } from './Processing/Processing'

type Values = {
  website: string
  websiteText: string
  topic: string
}

interface Props extends StepProps {
  setWebsiteText: (setWebsiteText: string) => void
}

export const ChooseTopic = multiStep<Props>(({ initialValues, onSubmit, setWebsiteText }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [scrapingError, setScrapingError] = useState(false)
  const { isMobile } = useResponsiveDesign()

  const schema = (scrapingError ? scrapeErrorStep : topicStep) as StepSchema
  const { meta } = schema.describe()

  const { fetchReference, handleCancel } = useFetchReference()

  const onAbortScanWebsite = () => {
    const error = new Error('Scanning website was aborted.')
    reportError(error)
  }

  const sectionHeader = scrapingError ? "We couldn't analyze your website" : 'Tell us about your company and goals'
  const sectionSubHeader = scrapingError ? undefined : "We'll create a content plan with ideas for you to choose from "

  const onCancelScanWebsite = () => {
    handleCancel()
    setIsLoading(false)
  }

  const handleSubmit = async (values: Values, submitForm: () => Promise<void>) => {
    if (!values.website && !values.topic) {
      return
    }

    try {
      setIsLoading(true)

      let websiteText
      if (!values.website) {
        websiteText = values.topic
      } else {
        websiteText = await fetchReference(values.website, onAbortScanWebsite)
        if (!websiteText) {
          if (isLoading) setScrapingError(true)
          return
        }
      }

      setWebsiteText(`${websiteText}\n\n ${values.topic}`)
      submitForm()
    } catch (error) {
      setScrapingError(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Formik
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
      >
        {({ submitForm, values }) => {
          const inputValues = values as Values
          const isUrlValid = isUrlCrawlable(inputValues.website).isValid

          return (
            <Form.Root>
              {isLoading && (
                <Form.Section>
                  <div className={Styles.processingWebsite}>
                    <Processing onCancel={onCancelScanWebsite} />
                  </div>
                </Form.Section>
              )}
              {!isLoading && (
                <Sections.Root schema={schema}>
                  <Sections.AutoFillFieldsSection
                    header={<Header header={sectionHeader} subHeader={sectionSubHeader} isRequired />}
                  >
                    <ResponsiveDesign.Mobile>
                      <LanguageInputField />
                    </ResponsiveDesign.Mobile>
                  </Sections.AutoFillFieldsSection>
                  <Form.Actions isSticky>
                    {scrapingError && (
                      <Form.PreviousButton onPress={() => setScrapingError(false)}>
                        {isMobile ? '' : 'Back'}
                      </Form.PreviousButton>
                    )}
                    <Form.PrimaryActions>
                      <ResponsiveDesign.Desktop>
                        <InlineLanguageInputField />
                      </ResponsiveDesign.Desktop>
                      <Button
                        size="lg"
                        isDisabled={(!inputValues.topic && !inputValues.website) || !isUrlValid}
                        onPress={() => {
                          handleSubmit(inputValues, submitForm)
                        }}
                        fullWidth={isMobile}
                      >
                        {meta.submitLabel}
                      </Button>
                    </Form.PrimaryActions>
                  </Form.Actions>
                </Sections.Root>
              )}
            </Form.Root>
          )
        }}
      </Formik>
    </>
  )
})
